import { FC, ReactNode } from 'react';
import ReactTooltip from 'react-tooltip';
import { PaymentStatus } from 'interfaces/stripe';

interface PaymentStatusBadgeProps {
  status: PaymentStatus | null;
  tooltipId?: string;
  tooltip?: ReactNode;
}

const STATUS_DISPLAY: Record<PaymentStatus, { label: string; className: string }> = {
  [PaymentStatus.HELD]: {
    label: 'Held',
    className: 'text-dim-grey bg-[#F3F3F3]',
  },
  [PaymentStatus.CAPTURED]: {
    label: 'Charged',
    className: 'text-secondary-red bg-[#FDF1F6]',
  },
  [PaymentStatus.RELEASED]: {
    label: 'Released',
    className: 'text-[#00A63E] bg-[#F0FDF4]',
  },
  [PaymentStatus.REFUNDED]: {
    label: 'Refunded',
    className: 'text-dim-grey bg-[#F3F3F3]',
  },
  [PaymentStatus.AUTH_EXPIRED]: {
    label: 'Expired',
    className: 'text-[#C58907] bg-[#FEFAF0]',
  },
  [PaymentStatus.FAILED]: {
    label: 'Failed',
    className: 'text-secondary-red bg-[#FDF1F6]',
  },
  [PaymentStatus.DISPUTED]: {
    label: 'Disputed',
    className: 'text-dim-grey bg-[#F3F3F3]',
  },
};

const PaymentStatusBadge: FC<PaymentStatusBadgeProps> = ({
  status,
  tooltipId,
  tooltip,
}) => {
  if (!status) {
    return null;
  }

  const { label, className } = STATUS_DISPLAY[status];
  const hasTooltip = !!tooltipId && !!tooltip;

  const badge = (
    <div
      className={`w-max h-2.2 inline-flex items-center gap-x-0.6 rounded-full border-[0.3px] px-1 py-0.2 text-12${hasTooltip ? ' cursor-help' : ''} ${className}`}
      data-for={hasTooltip ? tooltipId : undefined}
      data-tip={hasTooltip ? '' : undefined}
    >
      {label}
    </div>
  );

  if (!hasTooltip) {
    return badge;
  }

  return (
    <>
      {badge}
      <ReactTooltip
        id={tooltipId}
        place="bottom"
        effect="solid"
        className="!bg-white !rounded-[0.8rem] !w-[21rem] !h-[auto] z-[9999] !opacity-100 shadow-elevation-07 after:!border-b-white !px-1 !py-0.8"
      >
        {tooltip}
      </ReactTooltip>
    </>
  );
};

export default PaymentStatusBadge;
